import { ui } from "@workspace/mobile";
import { NavigateFunction } from "react-router-dom";

/**
 * Simple example function on how you might handle a universal link.
 * A universal link will be any URL, which will be bespoke to your application.
 * The link could be a root URL for your site, such as https://myapplication.accessacloud.com
 * Or it could contain additional data like subscription data or record ids, such as
 * https://myapplication.accessacloud.com/subscription/b07fd7bc-115e-4afb-998d-4e9e957d82f2/user/7b0f3708-9eea-492b-a3e5-4753085e6b69
 * You will need to parse out the information to use.
 * @param link The link that the user has tapped on that started the application or resumed it
 */
export const handleUniversalLink = async (
  link: string,
  navigate: NavigateFunction
) => {
  try {
    const url = new URL(link);
    const { pathname } = url;

    if (pathname.startsWith("/mobile")) {
      // Navigate to home page
      navigate("/");
    } else if (pathname.indexOf("subscription/") >= 0) {
      // Handle subscription routing (already exists)
      const splitForSubscription = pathname.split("subscription/");
      const urlSubscriptionId = splitForSubscription[1].split("/")[0];
      // ... existing subscription logic
      // Here you might navigate the user to the link provided,
      // for demonstration purposes, we'll just display an alert
      await ui.displayAlert({
        title: "Universal Link Received",
        message: `${link} contains subscription id ${urlSubscriptionId}`,
        accept: "Okay"
      });
    } else {
      // Handle unrecognized paths
      console.warn(`Universal link path not recognized: ${pathname}`);
      await ui.displayAlert({
        title: "Link Not Supported",
        message: `This link (${link}) is not currently supported in the app.`,
        accept: "OK"
      });
    }
  } catch (error) {
    console.error("Error handling universal link:", error);
    await ui.displayAlert({
      title: "Invalid Link",
      message: `The link (${link}) you clicked could not be opened. Please try again.`,
      accept: "OK"
    });
  }
};
/**
 * Function to handle subscription switching before any handling occurs
 * @param link The link that the user has tapped on that started the application or resumed it
 * @param subscriptionId The current active subscription id
 * @param switchSubscription Callback to fire the switch of a subscription in the user context
 * @param promptUser In most cases, we will want to ask the user if they want to switch
 * @returns boolean indicating if the handling should proceed
 */
export const trySwitchSubscriptionFromLink = async (
  link: string,
  currentSubscriptionId: string,
  switchSubscription: (subscriptionId: string) => void,
  promptUser = true
): Promise<boolean> => {
  try {
    const url = new URL(link);
    const { pathname } = url;

    // For the purposes of this example, it will presume the URL structure as above.
    // This can be tested using the browser console and the dev kit debug api, e.g.
    // mobileApiDevKit.universalLink("http://localhost:3000/subscription/ENTER_A_VALID_SUBSCRIPTION_ID_HERE_FOR_YOUR_USER")

    // We might check for the subscription within the URL
    if (pathname.indexOf("subscription/") >= 0) {
      // split on the subscription id text part
      const splitForSubscription = pathname.split("subscription/");
      // take the second part
      const urlSubscriptionId = splitForSubscription[1].split("/")[0];

      // No subscription id in the url, just return.
      if (!urlSubscriptionId) {
        return true;
      }

      // We can now compare the subscription ID to that found in the context
      // If it doesn't match, we most likely want to prompt the user to ask them
      // if they would like to switch the new org.
      // This should be dismissable, in case they were in the middle of a workflow
      // and could potentially lose data.

      // If we didn't have a known subscription, just make the switch
      if (!currentSubscriptionId) {
        switchSubscription(urlSubscriptionId);
        return true;
      }
      // Otherwise compare
      if (urlSubscriptionId !== currentSubscriptionId) {
        if (!promptUser) {
          switchSubscription(urlSubscriptionId);
          return true;
        }

        const makeSwitch = await ui.displayConfirm({
          title: "Switch Subscription",
          message: `The link you clicked was for another subscription, would you like to swap to that now?`,
          accept: "Okay",
          cancel: "Cancel"
        });

        // If they choose to make the switch to the new subscription you might need to
        // requery the organisation details, etc.
        if (makeSwitch) {
          switchSubscription(urlSubscriptionId);
        } else {
          return false;
        }
      }
    }
    return true;
  } catch {
    console.error("Error parsing the universal link URL");
    return false;
  }
};
